import React, { useState, useEffect } from 'react';
import { X, Star } from 'lucide-react';

const ReviewModal = ({ review, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    artist: '',
    type: 'album',
    rating: 5,
    review_text: '',
    release_date: '',
    concert_date: '',
    venue: '',
    listen_link: '',
    ticket_link: '',
    image_url: '',
    tags: []
  });

  const [tagInput, setTagInput] = useState('');

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (review) {
      setFormData({
        title: review.title || '',
        artist: review.artist || '',
        type: review.type || 'album',
        rating: review.rating || 5,
        review_text: review.review_text || '',
        release_date: review.release_date || '',
        concert_date: review.concert_date || '',
        venue: review.venue || '',
        listen_link: review.listen_link || '',
        ticket_link: review.ticket_link || '',
        image_url: review.image_url || '',
        tags: review.tags || []
      });
    }
  }, [review]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (!formData.tags.includes(newTag)) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Titel er påkrævet';
    }
    
    if (!formData.artist.trim()) {
      newErrors.artist = 'Artist er påkrævet';
    }
    
    if (!formData.review_text.trim()) {
      newErrors.review_text = 'Anmeldelse tekst er påkrævet';
    }
    
    if (formData.rating < 1 || formData.rating > 10) {
      newErrors.rating = 'Rating skal være mellem 1 og 10';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  const renderStarRating = () => {
    return (
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        {Array.from({ length: 10 }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => handleRatingChange(i + 1)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '2px'
            }}
          >
            <Star
              size={24}
              fill={i < formData.rating ? '#ffd700' : 'none'}
              color={i < formData.rating ? '#ffd700' : '#404040'}
            />
          </button>
        ))}
        <span style={{ marginLeft: '12px', color: '#ccc' }}>
          {formData.rating}/10
        </span>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal review-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {review ? 'Rediger anmeldelse' : 'Opret ny anmeldelse'}
          </h2>
          <button onClick={onClose} className="close-btn">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Titel *</label>
            <input
              type="text"
              id="title"
              name="title"
              className="form-control"
              value={formData.title}
              onChange={handleChange}
              placeholder="Indtast titel på album eller koncert"
            />
            {errors.title && (
              <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                {errors.title}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="artist">Artist *</label>
            <input
              type="text"
              id="artist"
              name="artist"
              className="form-control"
              value={formData.artist}
              onChange={handleChange}
              placeholder="Indtast artist navn"
            />
            {errors.artist && (
              <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                {errors.artist}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              name="type"
              className="form-control"
              value={formData.type}
              onChange={handleChange}
            >
              <option value="album">Album</option>
              <option value="koncert">Koncert</option>
            </select>
          </div>

          <div className="form-group">
            <label>Rating *</label>
            {renderStarRating()}
            {errors.rating && (
              <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                {errors.rating}
              </div>
            )}
          </div>

          {formData.type === 'album' && (
            <div className="form-group">
              <label htmlFor="release_date">Udgivelsesdato</label>
              <input
                type="date"
                id="release_date"
                name="release_date"
                className="form-control"
                value={formData.release_date}
                onChange={handleChange}
              />
            </div>
          )}

          {formData.type === 'koncert' && (
            <>
              <div className="form-group">
                <label htmlFor="concert_date">Koncert dato</label>
                <input
                  type="date"
                  id="concert_date"
                  name="concert_date"
                  className="form-control"
                  value={formData.concert_date}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="venue">Spillested</label>
                <input
                  type="text"
                  id="venue"
                  name="venue"
                  className="form-control"
                  value={formData.venue}
                  onChange={handleChange}
                  placeholder="Indtast spillested"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="review_text">Anmeldelse *</label>
            <textarea
              id="review_text"
              name="review_text"
              className="form-control"
              value={formData.review_text}
              onChange={handleChange}
              placeholder="Skriv din anmeldelse her..."
              rows="8"
            />
            {errors.review_text && (
              <div style={{ color: '#dc3545', fontSize: '14px', marginTop: '4px' }}>
                {errors.review_text}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="listen_link">
              {formData.type === 'album' ? 'Link til at lytte' : 'Info link'}
            </label>
            <input
              type="url"
              id="listen_link"
              name="listen_link"
              className="form-control"
              value={formData.listen_link}
              onChange={handleChange}
              placeholder={formData.type === 'album' 
                ? 'https://spotify.com/...' 
                : 'https://...'
              }
            />
          </div>

          {formData.type === 'koncert' && (
            <div className="form-group">
              <label htmlFor="ticket_link">Billet link</label>
              <input
                type="url"
                id="ticket_link"
                name="ticket_link"
                className="form-control"
                value={formData.ticket_link}
                onChange={handleChange}
                placeholder="https://billetto.dk/..."
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="image_url">Billede URL</label>
            <input
              type="url"
              id="image_url"
              name="image_url"
              className="form-control"
              value={formData.image_url}
              onChange={handleChange}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags</label>
            <input
              type="text"
              id="tags"
              className="form-control"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleAddTag}
              placeholder="Tilføj tags (tryk Enter for at tilføje)"
            />
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px', 
              marginTop: '12px' 
            }}>
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  style={{
                    background: 'var(--primary-gradient)',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleRemoveTag(tag)}
                >
                  {tag}
                  <X size={14} />
                </span>
              ))}
            </div>
            {formData.tags.length === 0 && (
              <p style={{ 
                color: 'var(--text-muted)', 
                fontSize: '14px', 
                marginTop: '8px',
                fontStyle: 'italic'
              }}>
                Ingen tags tilføjet endnu
              </p>
            )}
          </div>

          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            marginTop: '32px'
          }}>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Annuller
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              {review ? 'Opdater' : 'Opret'} anmeldelse
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReviewModal;
